import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

/**
 * One-time celebration banner rendered on the Dashboard the first time
 * a newly-approved Early Access member visits after admin approval.
 *
 * Visibility rules (matches the "approved" state the backend derives
 * from ``users.is_private_test``):
 *
 *   * ``user.is_private_test === true``
 *   * ``user.private_tester_welcome_dismissed_at`` is falsy
 *   * user is not an admin (admins don't need the welcome)
 *   * user is not an ephemeral demo visitor
 *
 * Dismissal is idempotent — the backend stamps the ISO timestamp and
 * ignores subsequent requests. On revocation the backend clears the
 * timestamp so a future re-approval re-shows the banner exactly once.
 */
export default function EarlyAccessWelcomeBanner({ audience }) {
  const { user, refresh } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  const isAdmin = (user?.roles || []).includes("admin");
  const isPT = !!user?.is_private_test;
  const alreadyDismissedServerSide = !!user?.private_tester_welcome_dismissed_at;

  if (!user || isAdmin || !isPT || user.is_ephemeral) return null;
  if (alreadyDismissedServerSide || dismissed) return null;

  // Role-aware primary action: authors are nudged straight into posting
  // their first project (beta readers can only find them once a project
  // exists), everyone else lands on the marketplace to browse. Users
  // who hold both roles see the author-flavoured copy since posting is
  // the higher-value first action while the reader marketplace is
  // always one click away in the nav.
  const isAuthor = audience !== "reader" && (user?.roles || []).includes("author");
  const primary = isAuthor
    ? { to: "/jobs/new", label: "Post your first project" }
    : { to: "/jobs", label: "Explore real projects" };
  const body = isAuthor
    ? "Post your first project so beta readers can discover it and start reaching out."
    : "You can now explore real projects, connect with authors, and use the full beta experience.";

  const dismiss = async () => {
    setDismissed(true);
    try {
      await api.post("/private-tester-requests/me/dismiss-welcome", {});
      // Refresh /auth/me so a hard refresh continues to keep the banner
      // hidden without a second network trip.
      if (typeof refresh === "function") {
        try { await refresh(); } catch { /* silent */ }
      }
    } catch {
      // Non-blocking — the local state above still hides the banner for
      // this session; user can re-dismiss on the next load.
      toast.info("We couldn't save your dismissal — the banner may reappear next visit.");
    }
  };

  return (
    <div
      className="mb-6 rounded-2xl border-2 border-[hsl(var(--sb-forest))]/50 bg-[hsl(var(--sb-moss))]/40 px-4 py-4 md:px-5 md:py-5 flex flex-col md:flex-row md:items-center md:flex-wrap gap-3 md:gap-4 shadow-md"
      data-testid="early-access-welcome-banner"
      role="status"
    >
      <div className="w-10 h-10 rounded-full bg-[hsl(var(--sb-forest))] grid place-items-center shrink-0 text-[hsl(42_60%_96%)]">
        <Sparkles size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm md:text-[15px] font-semibold text-foreground leading-snug">
          Early Access is active — welcome to the live beta.
        </p>
        <p className="mt-1 text-xs md:text-sm text-foreground/80 leading-relaxed">
          {body}
        </p>
      </div>
      <div className="shrink-0 self-start md:self-center flex items-center gap-2">
        <Link
          to={primary.to}
          className="btn-primary whitespace-nowrap"
          data-testid="early-access-welcome-explore"
        >
          {primary.label} <ArrowRight size={14} />
        </Link>
        <button
          type="button"
          onClick={dismiss}
          className="rounded-full p-2 text-muted-foreground hover:bg-muted"
          aria-label="Dismiss"
          data-testid="early-access-welcome-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
